"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import { voiceAgentQueryKey } from "@/hooks/queries/voice-agent"
import { invalidateAfterVoiceAgentChange } from "@/hooks/queries/voice-trial"
import type { SignupOnboardingState } from "@/lib/onboarding/signup-onboarding"
import type { SignupOnboardingPersonalizationSource } from "@/lib/onboarding/personalization-source"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import {
  activateSignupOnboardingEmailAgentAction,
  approveSignupOnboardingAgentAction,
  composeSignupOnboardingPromptAction,
  dismissSignupOnboardingResumeAction,
  finishSignupOnboardingAction,
  linkSignupOnboardingVoiceAgentAction,
  personalizeSignupOnboardingPromptAction,
  regenerateSignupOnboardingPromptAction,
  restartSignupOnboardingAction,
  resumeSignupOnboardingAction,
  saveSignupOnboardingAction,
  skipSignupOnboardingAction,
  type SignupOnboardingView,
  updateSignupOnboardingEmailReplyCategoriesAction,
} from "@/server-actions/signup-onboarding"
import {
  shopifyOnboardingStorefrontQueryKey,
  signupOnboardingAgentQueryKey,
  signupOnboardingAgentSummariesQueryKey,
  signupOnboardingQueryKey,
} from "@/hooks/queries/signup-onboarding"
import { setShopifyDisplayDomainAction } from "@/server-actions/shopify/display-domain"
import { useTranslations } from "next-intl"
import { billingOverviewQueryPrefix } from "@/hooks/queries/billing-overview"


function useUpdateSignupOnboardingCache() {
  const queryClient = useQueryClient()
  return async (state: SignupOnboardingState) => {
    queryClient.setQueryData(signupOnboardingQueryKey, (current: unknown) => {
      if (!current || typeof current !== "object") return current
      return { ...current, state }
    })
    await queryClient.invalidateQueries({ queryKey: signupOnboardingQueryKey })
  }
}

function useOptimisticSignupOnboardingCache() {
  const queryClient = useQueryClient()

  return {
    update: async (state: SignupOnboardingState) => {
      await queryClient.cancelQueries({ queryKey: signupOnboardingQueryKey })
      const previous = queryClient.getQueryData<SignupOnboardingView>(
        signupOnboardingQueryKey,
      )
      queryClient.setQueryData(signupOnboardingQueryKey, (current: unknown) => {
        if (!current || typeof current !== "object") return current
        return { ...current, state }
      })
      return { previous, state }
    },
    rollback: (context: {
      previous: SignupOnboardingView | undefined
      state: SignupOnboardingState
    } | undefined) => {
      if (!context) return

      queryClient.setQueryData(signupOnboardingQueryKey, (current: unknown) => {
        if (!current || typeof current !== "object") return current
        const currentState = (current as { state?: unknown }).state
        return currentState === context.state ? context.previous : current
      })
    },
  }
}

export function useSaveSignupOnboardingMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const optimisticCache = useOptimisticSignupOnboardingCache()
  return useMutation({
    scope: { id: "signup-onboarding-state" },
    mutationFn: async (state: SignupOnboardingState) => {
      const result = await withQueryHandling(
        saveSignupOnboardingAction({ state }),
        {
          showLoadingToast: false,
          showSuccessToast: false,
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result.state
    },
    onMutate: optimisticCache.update,
    onError: (_error, _state, context) => optimisticCache.rollback(context),
  })
}

export function useRestartSignupOnboardingMutation() {
  return useStateActionMutation(
    () => restartSignupOnboardingAction({}),
    "Restarting onboarding...",
  )
}

function useStateActionMutation(
  action: () => Promise<{ state: SignupOnboardingState } | { status: number; errorMessage: string }>,
  loading: string,
) {
  const withQueryHandling = useTranslatedQueryHandling()

  const updateCache = useUpdateSignupOnboardingCache()
  return useMutation({
    mutationFn: async () => {
      const result = await withQueryHandling(action(), { loading })
      if ("status" in result) throw new Error(result.errorMessage)
      return result.state
    },
    onSuccess: updateCache,
  })
}

export function useComposeSignupOnboardingPromptMutation() {
  const queryClient = useQueryClient()
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsSignupOnboarding")

  const updateCache = useUpdateSignupOnboardingCache()
  return useMutation({
    mutationFn: async (clientId: string) => {
      const result = await withQueryHandling(
        composeSignupOnboardingPromptAction({ client_id: clientId }),
        { loading: tUi("preparingYourAgentPrompt") },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result.state
    },
    onSuccess: updateCache,
    onSettled: () => queryClient.invalidateQueries({ queryKey: billingOverviewQueryPrefix }),
  })
}

export function usePersonalizeSignupOnboardingPromptMutation(
  knowledgeSource: SignupOnboardingPersonalizationSource,
) {
  const queryClient = useQueryClient()
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsSignupOnboarding")
  const updateCache = useUpdateSignupOnboardingCache()
  const toastMessages = getSignupOnboardingPersonalizationToastMessages(
    knowledgeSource, tUi,
  )
  return useMutation({
    mutationFn: async (clientId: string) => {
      const result = await withQueryHandling(
        personalizeSignupOnboardingPromptAction({ client_id: clientId }),
        toastMessages,
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result.state
    },
    onSuccess: updateCache,
    onSettled: () => queryClient.invalidateQueries({ queryKey: billingOverviewQueryPrefix }),
  })
}

export function getSignupOnboardingPersonalizationToastMessages(
  knowledgeSource: SignupOnboardingPersonalizationSource, tUi: ReturnType<typeof useTranslations<"Feedback.MutationsSignupOnboarding">>
): { loading: string; showLoadingToast?: boolean; success: string } {
  switch (knowledgeSource) {
    case "website":
      return {
        loading: tUi("personalizingYourAgentFromYourWebsite"),
        success: tUi("agentPersonalizedFromYourWebsite"),
      }
    case "documents":
      return {
        loading: tUi("personalizingYourAgentFromYourDocuments"),
        success: tUi("agentPersonalizedFromYourDocuments"),
      }
    case "social":
      return {
        loading: tUi("personalizingYourAgentFromYourSocialProfile"),
        showLoadingToast: false,
        success: tUi("agentPersonalizedFromYourSocialProfile"),
      }
    case "shopify":
      return {
        loading: tUi("personalizingYourAgentFromYourShopifyStore"),
        success: tUi("agentPersonalizedFromYourStore"),
      }
    default: {
      const exhaustiveSource: never = knowledgeSource
      return exhaustiveSource
    }
  }
}

export function useSetShopifyDisplayDomainMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsSignupOnboarding")

  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (domain: string) => {
      const result = await withQueryHandling(setShopifyDisplayDomainAction({ domain }), {
        loading: tUi("updatingHowYourStoreIsShown"),
        success: tUi("storeDomainUpdated"),
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result.probe
    },
    // The action already read the store; invalidating would pay for it a second time.
    onSuccess: (probe) =>
      queryClient.setQueryData(shopifyOnboardingStorefrontQueryKey, probe),
  })
}

export function useRegenerateSignupOnboardingPromptMutation() {
  const queryClient = useQueryClient()
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsSignupOnboarding")

  const updateCache = useUpdateSignupOnboardingCache()
  return useMutation({
    mutationFn: async (input: {
      client_id: string
      business_facts: string[]
      languages: string[]
    }) => {
      const result = await withQueryHandling(
        regenerateSignupOnboardingPromptAction(input),
        {
          loading: tUi("reGeneratingYourAgentPrompt"),
          success: tUi("agentPromptReGenerated"),
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result.state
    },
    onSuccess: updateCache,
    onSettled: () => queryClient.invalidateQueries({ queryKey: billingOverviewQueryPrefix }),
  })
}

export function useApproveSignupOnboardingAgentMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const queryClient = useQueryClient()
  const updateCache = useUpdateSignupOnboardingCache()
  return useMutation({
    mutationFn: async (input: {
      client_id: string
      agent_type?: "messaging" | "email" | "voice"
      agent_name: string
      approved_prompt: string
      business_summary: string | null
      languages: string[]
      prompt_choice: "original" | "enriched"
    }) => {
      const result = await withQueryHandling(
        approveSignupOnboardingAgentAction(input),
        {
          loading:
            input.agent_type === "voice"
              ? "Saving your Phone Voice Agent prompt..."
              : `Creating your ${input.agent_type === "email" ? "Email" : "Messaging"} Agent...`,
          success:
            input.agent_type === "voice"
              ? "Phone Voice Agent prompt approved"
              : `${input.agent_type === "email" ? "Email" : "Messaging"} Agent created`,
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async (result, input) => {
      await updateCache(result.state)
      await queryClient.invalidateQueries({
        queryKey: signupOnboardingAgentQueryKey(input.client_id),
      })
    },
  })
}

export function useCreateSignupOnboardingVoiceAgentMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsSignupOnboarding")

  const { fetchWithAuth } = useAuth()
  const queryClient = useQueryClient()
  const updateCache = useUpdateSignupOnboardingCache()

  return useMutation({
    mutationFn: async (input: {
      client_id: string
      name: string
      system_prompt: string
      first_message: string
      voice_id: string
      languages: string[]
      roles: string[]
      role_label: string | null
    }) =>
      withQueryHandling(
        (async () => {
          const response = await fetchWithAuth("/api/agents/voice", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              onboarding_client_id: input.client_id,
              name: input.name,
              system_prompt: input.system_prompt,
              first_message: input.first_message,
              voice_id: input.voice_id,
              languages: input.languages,
              roles: input.roles,
              role_label: input.role_label,
            }),
          })
          const result = (await response.json()) as {
            agent?: { id: string }
            error?: string
          }
          if (!response.ok || !result.agent) {
            throw new Error(result.error ?? "Failed to create Phone Voice Agent")
          }

          const linked = await linkSignupOnboardingVoiceAgentAction({
            client_id: input.client_id,
            agent_id: result.agent.id,
          })
          if ("status" in linked) {
            // Avoid leaving an untracked agent if the onboarding link fails.
            await fetchWithAuth(`/api/agents/voice/${result.agent.id}`, {
              method: "DELETE",
            }).catch(() => undefined)
            throw new Error(linked.errorMessage)
          }
          return { agent: result.agent, state: linked.state }
        })(),
        {
          loading: tUi("creatingYourPhoneVoiceAgent"),
          success: tUi("phoneVoiceAgentCreated"),
        },
      ),
    onSuccess: async (result, input) => {
      await updateCache(result.state)
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: signupOnboardingAgentQueryKey(input.client_id),
        }),
        queryClient.invalidateQueries({
          queryKey: signupOnboardingAgentSummariesQueryKey,
        }),
      ])
    },
    // A failed step deletes the agent it created, so both outcomes change the voice agents.
    onSettled: () => invalidateAfterVoiceAgentChange(queryClient),
  })
}

export function useUpdateSignupOnboardingVoiceAgentMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsSignupOnboarding")

  const { fetchWithAuth } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      agent_id: string
      name?: string
      system_prompt?: string
      first_message?: string
      voice_id?: string
      languages?: string[]
    }) =>
      withQueryHandling(
        (async () => {
          const response = await fetchWithAuth(
            `/api/agents/voice/${input.agent_id}`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                name: input.name,
                system_prompt: input.system_prompt,
                first_message: input.first_message,
                voice_id: input.voice_id,
                languages: input.languages,
              }),
            },
          )
          const result = (await response.json()) as {
            agent?: { id: string }
            error?: string
          }
          if (!response.ok || !result.agent) {
            throw new Error(
              result.error ?? "Failed to update Phone Voice Agent.",
            )
          }
          return result.agent
        })(),
        {
          loading: tUi("updatingYourPhoneVoiceAgent"),
          success: tUi("phoneVoiceAgentUpdated"),
        },
      ),
    onSuccess: async (_agent, input) => {
      await queryClient.invalidateQueries({
        queryKey: voiceAgentQueryKey(input.agent_id),
      })
    },
  })
}

export function useActivateSignupOnboardingEmailAgentMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsSignupOnboarding")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ clientId }: { clientId: string }) =>
      withQueryHandling(
        activateSignupOnboardingEmailAgentAction({ client_id: clientId }).then(
          (result) => {
            if ("errorMessage" in result) throw new Error(result.errorMessage)
            return result
          },
        ),
        {
          loading: tUi("enablingYourEmailAgent"),
          success: tUi("emailAgentReadyForUse"),
        },
      ),
    onSuccess: async (_agent, input) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: signupOnboardingAgentQueryKey(input.clientId),
        }),
        queryClient.invalidateQueries({
          queryKey: signupOnboardingAgentSummariesQueryKey,
        }),
      ])
    },
  })
}

export function useUpdateSignupOnboardingEmailCategoriesMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsSignupOnboarding")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      clientId: string
      selectedCategories: string[]
    }) => {
      const result = await withQueryHandling(
        updateSignupOnboardingEmailReplyCategoriesAction({
          client_id: input.clientId,
          selected_categories: input.selectedCategories,
        }),
        { loading: tUi("savingReplyCategories") },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result.config
    },
    onSuccess: async (_config, input) => {
      await queryClient.invalidateQueries({
        queryKey: signupOnboardingAgentQueryKey(input.clientId),
      })
    },
  })
}

export function useActivateSignupOnboardingMessagingAgentMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsSignupOnboarding")

  const { fetchWithAuth } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentId,
      clientId: _clientId,
    }: {
      agentId: string
      clientId: string
    }) =>
      withQueryHandling(
        fetchWithAuth(`/api/agents/messaging/${agentId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "active" }),
        }).then(async (response) => {
          const result = (await response.json()) as {
            agent?: Record<string, unknown>
            error?: string
          }
          if (!response.ok) {
            throw new Error(
              result.error ?? "Failed to activate Messaging Agent",
            )
          }
          return result.agent ?? {}
        }),
        {
          loading: tUi("activatingYourMessagingAgent"),
          success: tUi("messagingAgentReadyForUse"),
        },
      ),
    onSuccess: async (_agent, input) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: signupOnboardingAgentQueryKey(input.clientId),
        }),
        queryClient.invalidateQueries({
          queryKey: signupOnboardingAgentSummariesQueryKey,
        }),
      ])
    },
  })
}

export function useFinishSignupOnboardingMutation() {
  return useStateActionMutation(
    () => finishSignupOnboardingAction({}),
    "Finishing setup...",
  )
}

export function useSkipSignupOnboardingMutation() {
  return useStateActionMutation(
    () => skipSignupOnboardingAction({}),
    "Skipping onboarding...",
  )
}

export function useResumeSignupOnboardingMutation() {
  return useStateActionMutation(
    () => resumeSignupOnboardingAction({}),
    "Opening onboarding...",
  )
}

export function useDismissSignupOnboardingResumeMutation() {
  return useStateActionMutation(
    () => dismissSignupOnboardingResumeAction({}),
    "Updating preference...",
  )
}
